import React from "react";
import { CalendarX2, MapPin } from "lucide-react";

const ReservationPanel = ({ reservation }) => {
  if (!reservation) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6 text-gray-500 dark:text-gray-400">
        <div className="w-10 h-10 md:w-14 md:h-14 rounded-2xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center mb-3">
          <CalendarX2 className="w-6 h-6 md:w-8 md:h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <p className="font-semibold text-sm md:text-base">No Reservation</p>
        <p className="text-xs md:text-sm mt-1">
          This conversation has no linked reservation.
        </p>
      </div>
    );
  }

  const currency =
    !reservation.currency || reservation.currency === "usd" ? "$" : "﷼";

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const basePrice =
    reservation.nightsPrices?.reduce((sum, n) => sum + (n.total || 0), 0) || 0;

  return (
    <div className="flex flex-col gap-3 md:gap-4 p-3 md:p-4 overflow-y-auto h-full">
      {/* Property image + title */}
      <div className="flex gap-2 md:gap-3">
        <div
          className="w-16 h-16 md:w-24 md:h-24 rounded-lg bg-gray-200 flex-shrink-0"
          style={{
            backgroundImage: reservation.property?.photos?.[0]
              ? `url(${reservation.property.photos[0]})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="flex flex-col gap-1 min-w-0">
          <p className="font-bold text-xs md:text-sm line-clamp-2 dark:text-white">
            {reservation.property?.title || "Property"}
          </p>
          {reservation.property?.location && (
            <div className="flex items-start gap-1">
              <MapPin className="w-3 h-3 md:w-4 md:h-4 text-primaryColor mt-0.5 flex-shrink-0" />
              <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                {reservation.property.location.country?.en}
                {reservation.property.location.state
                  ? `, ${reservation.property.location.state}`
                  : ""}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Check-in / Check-out */}
      <div className="flex items-center justify-between text-xs md:text-sm">
        <div>
          <p className="font-semibold dark:text-white">
            {formatDate(reservation.checkIn)}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[10px] md:text-xs">
            Check-in
          </p>
        </div>
        <div className="text-gray-300 dark:text-gray-600">→</div>
        <div className="text-right">
          <p className="font-semibold dark:text-white">
            {formatDate(reservation.checkOut)}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[10px] md:text-xs">
            Check-out
          </p>
        </div>
      </div>

      <hr className="dark:border-gray-700" />

      {/* Price details */}
      <div className="flex flex-col gap-2">
        <p className="font-semibold text-xs md:text-sm dark:text-white">
          Price Details
        </p>

        <div className="flex justify-between text-xs md:text-sm">
          <span className="text-gray-600 dark:text-gray-400">Base price</span>
          <span className="dark:text-white">
            {currency}
            {basePrice}
          </span>
        </div>

        {reservation.discount > 0 && (
          <div className="flex justify-between text-xs md:text-sm">
            <span className="text-gray-600 dark:text-gray-400">Discount</span>
            <span className="dark:text-white">
              -{currency}
              {reservation.discount}
            </span>
          </div>
        )}

        {reservation.coupon && (
          <div className="flex justify-between text-xs md:text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Coupon ({reservation.coupon.code})
            </span>
            <span className="dark:text-white">
              -{currency}
              {Number(reservation.coupon.discountAmount).toFixed(2)}
            </span>
          </div>
        )}

        {reservation.fees?.map((fee, i) => (
          <div key={i} className="flex justify-between text-xs md:text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {fee.feeType}
            </span>
            <span className="dark:text-white">
              {currency}
              {Number(fee.amount).toFixed(2)}
            </span>
          </div>
        ))}

        {reservation.total != null && (
          <>
            <hr className="dark:border-gray-700" />
            <div className="flex justify-between text-xs md:text-sm font-bold">
              <span className="dark:text-white">Total</span>
              <span className="dark:text-white">
                {currency}
                {Number(reservation.total).toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Status badges */}
      <div className="flex gap-2 flex-wrap">
        {reservation.reservationState && (
          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            {reservation.reservationState}
          </span>
        )}
        {reservation.paymentState && (
          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
            {reservation.paymentState}
          </span>
        )}
      </div>
    </div>
  );
};

export default ReservationPanel;
